import { useEffect } from 'react';
import { JxtOffCanvas } from '@jooxter/ui';
import { OffCanvasKindEnum, useOffCanvasStore } from '../../store';
import { useStore } from '../../store/useStore';
import { useJxtRouter } from '../../routing/context';
import { useBookingOffCanvasContent } from '../../hooks/bookings/useBookingOffCanvasContent';
import { useFloorOffCanvasContent } from '../../hooks/floors/useFloorOffCanvasContent';
import { useResourceOffCanvasContent } from '../../hooks/resources/useResourceOffCanvasContent';
import { useWorkplaceCalendarOffCanvasContent } from '../../hooks/useWorkplaceCalendarOffCanvasContent';
import { IOffCanvasHost } from './types';

// The single mount point for the off-canvas, rendered once at the app root - including on routes
// that render before or without a session (login, right after logout). The content hooks below
// fetch booking/resource/floor data that needs an authenticated session, and some of those queries
// (e.g. useFetchResourceTypes) have no `enabled` guard of their own because they were never meant to
// run outside an authenticated page. Rather than auditing every query inside every content hook for
// an auth guard - and re-auditing it again the next time one is added - `AuthenticatedOffCanvasHost`
// below simply isn't mounted until there is a token, so none of its fetches ever fire logged out.
export const OffCanvasHost = ({ container }: IOffCanvasHost) => {
  const [token] = useStore((state) => [state.token]);
  const close = useOffCanvasStore((state) => state.close);
  const { location } = useJxtRouter();

  // Closing on navigation is a single, app-wide rule - not something each domain needs to know
  // about, so it lives here once instead of being duplicated per content hook. `close` is a
  // stable Zustand action (unlike the old context's `close`, rebuilt every render), so listing it
  // here doesn't make this effect re-fire on anything but an actual `location` change.
  useEffect(() => {
    close();
  }, [location, close]);

  if (!token) {
    return <JxtOffCanvas show={false} onHide={close} container={container} />;
  }

  return <AuthenticatedOffCanvasHost container={container} close={close} />;
};

const AuthenticatedOffCanvasHost = ({ container, close }: IOffCanvasHost & { close: () => void }) => {
  const { kind, isOpen, storeContainer } = useOffCanvasStore((state) => ({
    kind: state.kind,
    isOpen: state.isOpen,
    storeContainer: state.container,
  }));

  const booking = useBookingOffCanvasContent();
  const floor = useFloorOffCanvasContent();
  const resource = useResourceOffCanvasContent();
  const workplaceCalendar = useWorkplaceCalendarOffCanvasContent();

  const active =
    kind === OffCanvasKindEnum.Booking
      ? booking
      : kind === OffCanvasKindEnum.Floor
        ? floor
        : kind === OffCanvasKindEnum.Resource
          ? resource
          : kind === OffCanvasKindEnum.WorkplaceCalendar
            ? workplaceCalendar
            : undefined;

  return (
    <JxtOffCanvas
      show={isOpen}
      container={container ?? storeContainer}
      onHide={active?.onHide ?? close}
      header={active?.header}
      bodyClass={active?.bodyClass}
      tabConfiguration={active?.tabConfiguration}
    >
      {active?.children}
    </JxtOffCanvas>
  );
};
